import React, { useMemo, useRef, useState, useEffect } from 'react';
import Form from '../../../../common/Form';
import { useForm, FormProvider } from 'react-hook-form';
import AssetType from '../../../../components/Inputs/AssetType';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { addBoqInServiceRequest } from '../../../../actions/service_request';
import { actualValueForDropdown, getAssetColor } from '../../../../utils/common';
import IconToolTip from '../../../../common/IconToolTip';
import { copyToClipboard } from '../../../../actions/utils';
import PavDynamicAssetField from '../../../../features/pav/PavDynamicAssetField';
import { hasPavAssetFieldValue, serializePavAssetFieldValue } from '../../../../features/pav/pavAssetUtils';
const formName = 'add_boq_in_service_request';

export default function Boq({ currentMilestone, memoizedServiceRequest, toggleBoq }) {
    const submitRef = useRef();
    const methods = useForm();
    const dispatch = useDispatch();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const existingBoq = useMemo(() => {
        if (!memoizedServiceRequest.assets) return [];
        return memoizedServiceRequest.assets;
    }, [memoizedServiceRequest]);

    const submit = (formValues) => {
        const payload = {
            asset_type_id: actualValueForDropdown(formValues.asset_type_id),
            milestone_id: currentMilestone.id,
            ticket_id: memoizedServiceRequest.id
        };

        const additional_fields = formValues.asset_type_id.fields
            .filter((field) => hasPavAssetFieldValue(field, formValues[field.key]))
            .map((field) => ({
                key: field.key,
                value: serializePavAssetFieldValue(field, formValues[field.key])
            }));
        payload.additional_fields = additional_fields;

        dispatch(addBoqInServiceRequest({
            formName, payload, onSuccess: () => {
                methods.reset();
            }
        }));
    };

    const selectedAssetType = methods.watch('asset_type_id');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && !event.target.closest('.asset-type-dropdown')) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleAssetTypeClick = () => {
        setIsDropdownOpen(true);
    };

    return <motion.div
        initial={{ x: '-100vw' }}
        animate={{ x: 0 }}
        exit={{ x: '100vw' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="overflow-hidden bg-gradient-to-b from-gray-50 to-white relative"
    >
        <div className="p-3" style={{ maxHeight: '60vh' }}>
            {existingBoq.length > 0 && (
                <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">Existing BOQ Items</h3>
                    <div className='flex-wrap flex items-center gap-x-1 mt-2'>
                        {existingBoq.map((asset, index) => {
                            const status = `${asset?.status?.length > 0 ? ` ${asset?.status}` : ""}`
                            const workingStatus = `${asset?.working_condition?.length > 0 ? `, ${asset?.working_condition}` : ""}`;
                            return (
                                <IconToolTip key={index} title={`${status} ${workingStatus}`}>
                                    <span className={`bg-${getAssetColor(asset)} cursor-pointer text-white max-w-12 text-font10 px-2 flex items-center rounded-3px mb-1`}>
                                        <span className='max-w-4/5 truncate'>
                                            {(asset?.asset?.serial_number?.length > 0 ? asset?.asset?.serial_number : asset?.asset?.title)}
                                        </span>
                                        <span>
                                            {(asset?.count > 1 ? `: ${asset?.count}` : '')}
                                        </span>
                                        <span className="material-icons text-font12 ml-1 hover:text-scogobgsky" onClick={(event) => {
                                            event.stopPropagation();
                                            const text = asset?.asset?.serial_number?.length > 0 ? asset?.asset?.serial_number : asset?.asset?.title

                                            dispatch(copyToClipboard({ data: text }))
                                        }}>
                                            content_copy
                                        </span>
                                    </span>
                                </IconToolTip>
                            );
                        })}
                    </div>
                </div>
            )}

            <div>
                <FormProvider {...methods}>
                    <Form submitRef={submitRef} methods={methods} onSubmit={submit} >
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 relative max-h-48 overflow-y-auto'>
                            <div className="col-span-1 asset-type-dropdown" onClick={handleAssetTypeClick}>
                                <AssetType
                                    label='Asset Type'
                                    required
                                    name='asset_type_id'
                                    org_id={memoizedServiceRequest?.org_id}
                                    isOpen={isDropdownOpen}
                                    setIsOpen={setIsDropdownOpen}
                                    className="z-50"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        menu: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            {selectedAssetType && selectedAssetType.fields && selectedAssetType.fields.map((field) => (
                                <div key={field.key} className="col-span-1">
                                    <PavDynamicAssetField field={field} />
                                </div>
                            ))}
                        </div>
                    </Form>
                    <div className='flex justify-end gap-4 mt-6'>
                        <button
                            type="button"
                            className="px-5 py-2.5 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-all duration-200 font-medium flex items-center shadow-sm"
                            onClick={() => {
                                methods.reset();
                                toggleBoq();
                            }}
                        >
                            <span className="material-icons mr-1 text-sm">close</span>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="px-5 py-2.5 text-white bg-scogoprimary rounded-md hover:bg-scogodarker transition-all duration-200 font-medium flex items-center shadow-sm"
                            onClick={() => submitRef.current.click()}
                        >
                            <span className="material-icons mr-1 text-sm">add_circle</span>
                            Add Asset
                        </button>
                    </div>
                </FormProvider>
            </div>
        </div>
    </motion.div>
}
